import type { FC, ReactNode } from 'react'

import { ArrowDown } from 'lucide-react'
import { DiviWordmark } from '@/components/generals/divi-logo'
import { BRAND_COLORS } from '@/constants/brand'

const SPECS = [
    { label: "Lean", value: '12°' },
    { label: "Weight", value: "Heavy" },
    { label: "Core red", value: BRAND_COLORS.red }
]

export const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' className='relative isolate overflow-hidden'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -right-56 -top-64 size-[46rem] rounded-full bg-[radial-gradient(closest-side,rgba(46,116,214,0.42),transparent)]' />
                <div className='absolute -bottom-72 -left-48 size-[38rem] rounded-full bg-[radial-gradient(closest-side,rgba(28,62,140,0.22),transparent)]' />
                <div className='absolute left-1/2 top-[44%] h-80 w-[min(58rem,130vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(255,35,69,0.24),transparent)] motion-safe:animate-[ember-breathe_7s_ease-in-out_infinite]' />
                <div className='grain-texture absolute inset-0 opacity-30 mix-blend-overlay' />
                <div className='absolute inset-x-0 bottom-0 h-40 bg-linear-to-b from-transparent to-background' />
            </div>

            <div className='mx-auto flex min-h-[calc(100svh-4rem)] max-w-6xl flex-col items-center justify-center px-6 py-24 text-center'>
                <p className='font-mono text-xs uppercase tracking-[0.32em] text-muted-foreground motion-safe:animate-in motion-safe:fade-in motion-safe:duration-700'>
                    Logo system · Concept A
                </p>

                <h1 id='hero-title' className='mt-12 w-full motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-1000 motion-safe:ease-out'>
                    <span className='sr-only'>Divi</span>
                    <DiviWordmark
                        aria-hidden='true'
                        className='mx-auto h-auto w-[min(84vw,46rem)] drop-shadow-[0_24px_64px_rgba(255,35,69,0.3)]'
                    />
                </h1>

                <p className='mt-14 max-w-xl text-balance text-base leading-relaxed text-muted-foreground sm:text-lg motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:delay-200 motion-safe:duration-700 motion-safe:fill-mode-backwards'>
                    A heavy, forward-leaning wordmark with a shield-shaped V that doubles as a D. Drawn as clean vectors in Divi red, made to glow on dark.
                </p>

                <div className='mt-10 flex flex-wrap items-center justify-center gap-3'>
                    <a
                        href='#variations'
                        className='inline-flex h-12 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-[0_12px_32px_-12px_rgba(255,35,69,0.8)] transition-transform hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-red'
                    >
                        See the four variations
                        <ArrowDown aria-hidden='true' className='size-4' />
                    </a>

                    <a
                        href='#colors'
                        className='inline-flex h-12 items-center rounded-full border border-border bg-white/5 px-6 text-sm font-semibold text-foreground transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-red'
                    >
                        Brand colors
                    </a>
                </div>

                <dl className='mt-16 grid w-full max-w-md grid-cols-3 gap-4 border-t border-white/10 pt-6'>
                    {SPECS.map(spec => (
                        <div key={spec.label}>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground'>
                                {spec.label}
                            </dt>
                            <dd className='mt-2 font-heading text-sm font-semibold text-foreground'>
                                {spec.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </section>
    )
}
